import { logInfo, logError, logWarning } from "@/Utils/logger";
export const AUDIO_MAP = {
  moveBack: "/audio/all_move-back.mp3",
  moveCloser: "/audio/all_move-closer.mp3",
  tiltDeviceDown: "/audio/body_tilt-device-down.mp3",
  tiltDeviceUp: "/audio/body_tilt-device-up.mp3",
  correctPosture: "/audio/ChimeNoise.mp3",
  holdArmsUp: "/audio/hold-your-arms-up-and-get-ready.mp3",
  spin: "/audio/Ready-Set-Spin-trimmed.mp3",
  countdown: "/audio/trim.mp3",
  incompleteDataCapture: "/audio/Incomplete-data-capture.mp3",
  incompleteRotation: "/audio/Incomplete-rotation.mp3",
  overRotation: "/audio/Over-rotation.mp3",
  didNotRotateInPlace: "/audio/Did-not-rotate-in-place.mp3",
  deviceMoved: "/audio/Device-moved.mp3",
  rotatedTooQuickly: "/audio/Rotated-too-quickly.mp3",
  bodyNotDetected: "/audio/Body-not-detected.mp3",
  scanComplete: "/audio/all_scan-complete.mp3",
  placeDeviceAtFloorLevel: "/audio/body_place-your-device-at-floor-level.mp3",
};

export type AudioLoadStage =
  | "idle"
  | "closing-previous-context"
  | "html-audio-init"
  | "context-create"
  | "context-resume"
  | "fetch-decode"
  | "complete";

export class AudioManager {
  private static instance: AudioManager;
  private audioContext: AudioContext | null = null;
  // Stores decoded audio buffers when AudioContext is available
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  // Fallback using HTMLAudioElement
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  private activeSource: AudioBufferSourceNode | null = null;
  private activeAudioUrl: string | null = null;
  private onAllAudiosLoadedCallback: (() => void) | null = null;

  /**
   * Which step of `loadAudios()` is in flight. Read by the AssetLoadingService
   * watchdog when a load fails to settle, so the next BUG-0006 occurrence says
   * WHERE it hung — the one trace we have shows only that it was somewhere
   * after HTML Audio initialised and before the files were loaded.
   */
  private loadStage: AudioLoadStage = "idle";

  public getLoadStage(): AudioLoadStage {
    return this.loadStage;
  }
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // milliseconds
  private failedAudios: Set<string> = new Set();
  private loadingPromises: Map<string, Promise<void>> = new Map();
  private keepAliveOsc: OscillatorNode | null = null;
  private keepAliveGain: GainNode | null = null;
  private keepAliveAudio: HTMLAudioElement | null = null;
  private keepAliveSource: MediaElementAudioSourceNode | null = null;
  private onAudioEndCallback: ((audioUrl: string) => void) | null = null;

  private constructor() {
    if (AudioManager.instance) {
      throw new Error(
        "AudioManager is a singleton class. Use AudioManager.getInstance() to get the instance."
      );
    }
    // Ensure compatibility with Safari
    window.AudioContext =
      window.AudioContext || (window as any).webkitAudioContext;
    window.addEventListener("visibilitychange", () => {
      this.ensureContextRunning();
    });
  }

  private async createNewContext() {
    const Ctx = window.AudioContext;
    const ctx = new Ctx();
    this.loadStage = "context-resume";
    await this.resumeBounded(ctx);

    /* 🔈  keep‑alive  */
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0; // silence
    osc.connect(gain).connect(ctx.destination);
    osc.start(0);

    // remember them so we can stop/GC later
    this.keepAliveOsc = osc;
    this.keepAliveGain = gain;

    if (this.keepAliveAudio) {
      this.keepAliveSource = ctx.createMediaElementSource(this.keepAliveAudio);
      this.keepAliveSource.connect(ctx.destination);
    }

    this.audioContext = ctx;
  }

  /**
   * How long to wait for `AudioContext.resume()` before carrying on without it.
   * Generous against a slow but genuine resume; short against the iOS shape
   * where it never settles at all.
   */
  private readonly RESUME_TIMEOUT_MS = 3000;

  /**
   * `resume()` with a bound. The original code awaited it outright, with the
   * comment "already inside the user click" — which is not true of the
   * scan-screen preload (it runs from a navigation) nor of the boot-time
   * preload, and on iOS a `resume()` issued without transient user activation
   * neither resolves nor rejects. Loading does not need a running context:
   * `decodeAudioData` works on a suspended one, and playback goes through
   * `ensureContextRunning()` from the user's Start tap. So after the bound the
   * load proceeds with the context still suspended. A rejection is still a
   * rejection — the caller's fallback to `<audio>` is unchanged.
   */
  private resumeBounded(ctx: AudioContext): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        logWarning(
          "AudioContext.resume() did not settle — continuing with a suspended context",
          { timeoutMs: this.RESUME_TIMEOUT_MS, state: ctx.state }
        );
        resolve();
      }, this.RESUME_TIMEOUT_MS);
      ctx.resume().then(
        () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve();
        },
        (e) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(e);
        }
      );
    });
  }

  private async closeContext() {
    /* stop and disconnect the silent nodes */
    try {
      this.keepAliveOsc?.stop();
      this.keepAliveOsc?.disconnect();
      this.keepAliveGain?.disconnect();
    } catch (e) {
      logWarning("Failed to close keepAliveOsc", e as Error);
    }

    this.keepAliveOsc = null;
    this.keepAliveGain = null;

    try {
      this.keepAliveAudio?.pause();
      this.keepAliveAudio?.remove();
      this.keepAliveAudio = null;
    } catch (e) {
      logWarning("Failed to close keepAliveAudio", e as Error);
    }

    try {
      this.keepAliveSource?.disconnect();
    } catch (e) {
      logWarning("Failed to close keepAliveSource", e as Error);
    }

    this.keepAliveSource = null;

    /* close the context itself */
    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch (e) {
        logWarning("Failed to close audioContext", e as Error);
      }
      this.audioContext = null;
    }
  }

  private async ensureContextRunning() {
    if (!this.audioContext) return;

    if (this.audioContext.state !== "running") {
      try {
        await this.audioContext.resume();
      } catch (e) {
        logWarning(
          "AudioContext failed to resume; falling back to <audio>",
          e as Error
        );
        await this.closeContext(); // also stops the keep‑alive osc
      }
    }
  }

  /**
   * Preloads all audio files.
   * Clears any existing audio buffers or elements before loading.
   * This method is safe to call on a button click event.
   */
  public async loadAudios() {
    // Clear existing HTMLAudioElement instances
    this.audioElements.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
      audio.remove();
    });
    this.audioElements.clear();
    this.audioBuffers.clear();

    this.loadStage = "closing-previous-context";
    await this.closeContext();

    try {
      // Initialize HTML Audio to work in silent mode (must be done before AudioContext)
      try {
        this.loadStage = "html-audio-init";
        const audio = new Audio();
        // Use a minimal valid audio data URL
        audio.src = "audio/bg-noise.wav";
        audio.loop = true;
        audio.volume = 0.005; // ≠ 0 so iOS counts it
        audio.muted = false; // don’t mute – just tiny volume
        // @ts-ignore
        audio.playsInline = true;
        audio.setAttribute("playsinline", "true");
        audio.crossOrigin = "anonymous";
        await audio.play();
        this.keepAliveAudio = audio;

        logInfo("HTML Audio initialized successfully for silent mode support");
      } catch (e) {
        logWarning(
          "Failed to initialize HTML Audio for silent mode",
          e as Error
        );
      }

      this.loadStage = "context-create";
      await this.createNewContext();
    } catch (e) {
      logWarning(
        "AudioContext failed to initialize; falling back to <audio>",
        e as Error
      );
      this.audioContext = null;
    }

    // Clear previous loading state
    this.failedAudios.clear();
    this.loadingPromises.clear();
    this.loadStage = "fetch-decode";

    const loadPromises = Object.values(AUDIO_MAP).map((audioUrl) => {
      const loadPromise = this.audioContext
        ? this.loadAudioBufferWithRetry(audioUrl, this.MAX_RETRIES).catch(
            (error) => {
              logError(
                `Failed to load ${audioUrl} using AudioContext. Falling back to HTMLAudioElement. Error:`,
                error
              );
              return this.loadWithHtmlAudioWithRetry(
                audioUrl,
                this.MAX_RETRIES
              );
            }
          )
        : this.loadWithHtmlAudioWithRetry(audioUrl, this.MAX_RETRIES);

      this.loadingPromises.set(audioUrl, loadPromise);
      return loadPromise;
    });

    await Promise.all(loadPromises)
      .then(async () => {
        await this.handleAllAudiosLoadComplete();
      })
      .catch(async (error) => {
        await logError("An error occurred while loading audios:", error);
        await this.handleAllAudiosLoadComplete();
      });
    this.loadStage = "complete";
  }

  private async loadWithHtmlAudioWithRetry(
    audioUrl: string,
    retries: number
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl);

      const onLoad = () => {
        audio.removeEventListener("canplaythrough", onLoad);
        audio.removeEventListener("error", onError);
        this.audioElements.set(audioUrl, audio);
        resolve();
      };

      const onError = () => {
        audio.removeEventListener("canplaythrough", onLoad);
        audio.removeEventListener("error", onError);

        if (retries > 0) {
          logWarning(
            `Failed to load audio: ${audioUrl}. Retrying... (${
              this.MAX_RETRIES - retries + 1
            }/${this.MAX_RETRIES})`
          );
          setTimeout(() => {
            this.loadWithHtmlAudioWithRetry(audioUrl, retries - 1)
              .then(resolve)
              .catch(reject);
          }, this.RETRY_DELAY);
        } else {
          logError(
            `Failed to load audio after ${this.MAX_RETRIES} retries: ${audioUrl}`
          );
          this.failedAudios.add(audioUrl);
          reject(new Error(`Failed to load audio: ${audioUrl}`));
        }
      };

      audio.addEventListener("canplaythrough", onLoad);
      audio.addEventListener("error", onError);
      audio.load();
    });
  }

  private async handleAllAudiosLoadComplete(): Promise<void> {
    const totalAudios = Object.values(AUDIO_MAP).length;
    const loadedAudios = this.audioBuffers.size + this.audioElements.size;
    const failedAudios = this.failedAudios.size;

    logInfo(
      `Audio loading complete. Loaded: ${loadedAudios}, Failed: ${failedAudios}, Total: ${totalAudios}`
    );

    if (failedAudios > 0) {
      await logError(
        `Failed to load ${failedAudios} audio files after retries. Reloading page...`,
        {
          failedAudios,
          totalAudios,
          loadedAudios,
          failedAudiosUrls: Array.from(this.failedAudios),
        }
      );
      // Reload the page as a last resort
      setTimeout(() => {
        window.location.reload();
      }, 1000); // gives time for logs to be sent (will help debugging)
      return;
    }

    // All audios loaded successfully
    if (this.onAllAudiosLoadedCallback) {
      this.onAllAudiosLoadedCallback();
    }
  }

  public onAllAudiosLoaded(callback: () => void) {
    this.onAllAudiosLoadedCallback = callback;
  }

  /**
   * Loads and decodes an audio file using the AudioContext API.
   * Retries on failure until maximum retries are reached.
   */
  private async loadAudioBufferWithRetry(
    url: string,
    retries: number
  ): Promise<void> {
    if (!this.audioContext) return;
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.audioBuffers.set(url, audioBuffer);
    } catch (error) {
      if (retries > 0) {
        logWarning(`Retrying load of ${url}. Retries left: ${retries}`);
        await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY));
        return this.loadAudioBufferWithRetry(url, retries - 1);
      } else {
        throw error;
      }
    }
  }

  static getInstance() {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  /**
   * Stops any active audio playback, whether from AudioContext or HTMLAudioElement.
   */
  public stopActiveAudio(): void {
    if (this.activeSource) {
      this.activeSource.stop();
      this.activeSource = null;
    }
    if (this.activeAudioUrl) {
      const fallbackAudio = this.audioElements.get(this.activeAudioUrl);
      if (fallbackAudio) {
        fallbackAudio.pause();
        fallbackAudio.currentTime = 0;
      }
      this.activeAudioUrl = null;
    }
  }

  /**
   * Plays an audio given its URL. It will first try to use AudioContext if available.
   * If that fails, it falls back to using HTMLAudioElement.
   * Includes retry logic for play failures.
   */
  public async playAudio(audioUrl: string): Promise<void> {
    this.stopActiveAudio();
    if (this.audioContext && this.audioBuffers.has(audioUrl)) {
      return this.playWithAudioContext(
        audioUrl,
        this.MAX_RETRIES,
        this.onAudioEndCallback
      );
    } else {
      return this.playWithHtmlAudio(
        audioUrl,
        this.MAX_RETRIES,
        this.onAudioEndCallback
      );
    }
  }

  /**
   * Plays an audio using the AudioContext API with a retry mechanism.
   */
  private async playWithAudioContext(
    audioUrl: string,
    retries = this.MAX_RETRIES,
    onAudioEnd: ((audioUrl: string) => void) | null
  ): Promise<void> {
    if (!this.audioContext) {
      throw new Error("AudioContext is not available");
    }
    // resumes audioContext always to prevent the suspension of the audioContext
    // on Safari
    await this.ensureContextRunning();

    try {
      const audioBuffer = this.audioBuffers.get(audioUrl);
      if (!audioBuffer) {
        throw new Error(`AudioBuffer for ${audioUrl} not found`);
      }

      // Create and set up the source
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);

      // Set up completion handler before starting playback
      return new Promise((resolve, reject) => {
        source.onended = () => {
          resolve();
          if (onAudioEnd) {
            onAudioEnd(audioUrl);
          }
        };

        try {
          // Start playback
          source.start(0);
          this.activeSource = source;
          this.activeAudioUrl = audioUrl;
        } catch (startError) {
          logError(
            "Error starting audio with AudioContext",
            startError as Error
          );
          // Only if start() fails, we should retry or fall back
          if (retries > 0) {
            logWarning(
              `Retrying playWithAudioContext for ${audioUrl}. Retries left: ${retries}`
            );
            setTimeout(() => {
              this.playWithAudioContext(audioUrl, retries - 1, onAudioEnd)
                .then(resolve)
                .catch(reject);
            }, this.RETRY_DELAY);
          } else {
            logError(
              "Error starting audio with AudioContext, falling back to HTMLAudioElement",
              startError as Error
            );
            this.playWithHtmlAudio(audioUrl, retries, onAudioEnd)
              .then(resolve)
              .catch(reject);
          }
        }
      });
    } catch (setupError) {
      logError("Error setting up AudioContext playback", setupError as Error);
      // Handle errors in initial setup (getting buffer, creating source)
      if (retries > 0) {
        logWarning(
          `Retrying playWithAudioContext setup for ${audioUrl}. Retries left: ${retries}`
        );
        await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY));
        return this.playWithAudioContext(audioUrl, retries - 1, onAudioEnd);
      } else {
        logError(
          "Error setting up AudioContext playback, falling back to HTMLAudioElement",
          setupError as Error
        );
        return this.playWithHtmlAudio(audioUrl, retries, onAudioEnd);
      }
    }
  }

  /**
   * Plays an audio using HTMLAudioElement with a retry mechanism.
   */
  private async playWithHtmlAudio(
    audioUrl: string,
    retries = this.MAX_RETRIES,
    onAudioEnd: ((audioUrl: string) => void) | null
  ): Promise<void> {
    try {
      let audio = this.audioElements.get(audioUrl);
      if (!audio) {
        audio = new Audio(audioUrl);
        this.audioElements.set(audioUrl, audio);
      }
      audio.currentTime = 0;
      this.activeAudioUrl = audioUrl;
      audio.addEventListener("ended", () => {
        if (onAudioEnd) {
          onAudioEnd(audioUrl);
        }
      });
      await audio.play();
    } catch (error) {
      if (retries > 0) {
        logWarning(
          `Retrying playWithHtmlAudio for ${audioUrl}. Retries left: ${retries}`
        );
        await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY));
        return this.playWithHtmlAudio(audioUrl, retries - 1, onAudioEnd);
      } else {
        this.stopActiveAudio();
        logError("Error playing audio with HTMLAudioElement", error as Error);
        throw error;
      }
    }
  }

  public setOnAudioEndCallback(callback: (audioUrl: string) => void) {
    this.onAudioEndCallback = callback;
  }

  /**
   * Cleans up any active audio playback, stops all audio sources,
   * closes audio context, and resets all class properties.
   * This method ensures complete cleanup without breaking functionality.
   * @param preserveCallback - If true, preserves the onAudioEndCallback
   */
  public async cleanup(preserveCallback: boolean = false): Promise<void> {
    try {
      // Stop any active audio playback
      this.stopActiveAudio();

      // Stop and clean up all HTML audio elements
      this.audioElements.forEach((audio) => {
        try {
          audio.pause();
          audio.currentTime = 0;
          // Remove event listeners to prevent memory leaks
          audio.removeEventListener("ended", () => {});
          audio.removeEventListener("error", () => {});
          audio.src = "";
          audio.load(); // Reset the audio element
        } catch (error) {
          logWarning("Error cleaning up audio element", error as Error);
        }
      });

      // Close audio context and clean up keep-alive resources
      if (this.audioContext) {
        await this.closeContext();
      }

      // Clear all maps and reset properties
      this.audioBuffers.clear();
      this.audioElements.clear();
      this.failedAudios.clear();
      this.loadingPromises.clear();
      this.activeSource = null;
      this.activeAudioUrl = null;
      this.onAllAudiosLoadedCallback = null;
      if (!preserveCallback) {
        this.onAudioEndCallback = null;
      }
      this.keepAliveOsc = null;
      this.keepAliveGain = null;
      this.keepAliveAudio = null;
      this.keepAliveSource = null;
      this.audioContext = null;
    } catch (error) {
      logError("Error during AudioManager cleanup", error as Error);
      throw error;
    }
  }

  /**
   * Resets the AudioManager to a clean state after cleanup.
   * This method reinitializes the maps and prepares the manager for reuse.
   */
  public reset(): void {
    try {
      // Reinitialize the maps
      this.audioBuffers = new Map();
      this.audioElements = new Map();
      this.failedAudios = new Set();
      this.loadingPromises = new Map();

      // Reset all properties to their initial state
      this.activeSource = null;
      this.activeAudioUrl = null;
      this.onAllAudiosLoadedCallback = null;
      this.onAudioEndCallback = null;
      this.keepAliveOsc = null;
      this.keepAliveGain = null;
      this.keepAliveAudio = null;
      this.keepAliveSource = null;
      this.audioContext = null;
    } catch (error) {
      logError("Error during AudioManager reset", error as Error);
      throw error;
    }
  }

  /**
   * Convenience method that performs cleanup followed by reset.
   * This ensures the AudioManager is completely cleaned up and ready for reuse.
   * @param preserveCallback - If true, preserves the onAudioEndCallback during cleanup
   */
  public async cleanupAndReset(
    preserveCallback: boolean = false
  ): Promise<void> {
    await this.cleanup(preserveCallback);
    this.reset();
  }
}
